/**
 * Customer Map tab — every wash in the RinsePay network where the app works
 * (active location + activated, Stripe-onboarded owner), sorted by distance
 * from the customer when location access is granted.
 *
 * Map rendering is platform-split in WashMapView (web: Leaflet/OSM; native:
 * react-native-maps, with a graceful placeholder until the module is linked
 * in the next store build). The list + directions always work.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, Platform } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import { openExternal } from '../../src/utils/openExternal';
import WashMapView, { MapViewport } from '../../src/components/WashMapView';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { useToast } from '../../src/components/Toast';
import { useNetwork } from '../../src/context/NetworkContext';
import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';

interface WashLocation {
  id: string;
  name: string;
  businessName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  phone: string | null;
  hours: Record<string, { open: string; close: string }> | null;
}

/** Great-circle distance in miles. */
function milesBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(h));
}

/** Best-effort current position: web navigator API, native community module. */
function getPosition(): Promise<{ latitude: number; longitude: number } | null> {
  return new Promise(resolve => {
    let geo: any = null;
    if (Platform.OS === 'web') {
      geo = typeof navigator !== 'undefined' ? navigator.geolocation : null;
    } else {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        geo = require('@react-native-community/geolocation').default;
      } catch { /* not linked until the next store build */ }
    }
    if (!geo?.getCurrentPosition) return resolve(null);
    geo.getCurrentPosition(
      (pos: { coords: { latitude: number; longitude: number } }) =>
        resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),   // denied/unavailable — map still works, just unsorted
      { timeout: 8000, maximumAge: 300000 },
    );
  });
}

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export default function CustomerMapScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();

  const [washes, setWashes] = useState<WashLocation[]>([]);
  const [userLoc, setUserLoc] = useState<{ latitude: number; longitude: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True while a finger is on the map (native) — releases the page scroll so
  // map pans aren't stolen now that the map scrolls with the page.
  const [mapActive, setMapActive] = useState(false);
  // Visible map bounds; null until the map reports one (e.g. the native
  // placeholder tier never does) — the list stays unfiltered then.
  const [viewport, setViewport] = useState<MapViewport | null>(null);
  const viewportTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (viewportTimer.current) clearTimeout(viewportTimer.current); }, []);

  // Debounced (~300ms) so continuous pans/zooms don't re-filter every frame.
  const handleViewportChange = useCallback((v: MapViewport) => {
    if (viewportTimer.current) clearTimeout(viewportTimer.current);
    viewportTimer.current = setTimeout(() => setViewport(v), 300);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    // Position must NEVER gate the pins: getCurrentPosition's timeout only
    // starts after permission is granted, so an unanswered permission prompt
    // (or Permissions-Policy disabling geolocation) leaves it hanging forever
    // — which used to hang the whole screen via Promise.all. Render the map
    // first; distance sorting upgrades in place if/when a position arrives.
    Promise.race([
      getPosition(),
      new Promise<null>(r => setTimeout(() => r(null), 6000)),
    ]).then(pos => { if (pos) setUserLoc(pos); }).catch(() => { /* unsorted */ });
    try {
      const res = await api<{ locations: WashLocation[] }>('/api/wash-map');
      setWashes(res.locations);
    } catch (err: any) {
      if (!isOffline) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const sorted = useMemo(() => {
    const rows = washes.map(w => ({
      ...w,
      miles: userLoc ? milesBetween(userLoc.latitude, userLoc.longitude, w.latitude, w.longitude) : null,
    }));
    if (userLoc) rows.sort((a, b) => (a.miles ?? 0) - (b.miles ?? 0));
    return rows;
  }, [washes, userLoc]);

  const selected = selectedId ? sorted.find(w => w.id === selectedId) ?? null : null;

  // List rows restricted to the current map viewport (pins stay complete so
  // panning back always recovers them). Falls back to the full list whenever
  // viewport data is unavailable.
  const inView = useMemo(() => {
    if (!viewport) return sorted;
    const { north, south, east, west } = viewport;
    return sorted.filter(w => {
      const latOk = w.latitude >= south && w.latitude <= north;
      const lngOk = west <= east
        ? w.longitude >= west && w.longitude <= east
        : w.longitude >= west || w.longitude <= east; // anti-meridian wrap
      return latOk && lngOk;
    });
  }, [sorted, viewport]);

  const todayHours = (w: WashLocation): string | null => {
    if (!w.hours) return null;
    const h = w.hours[DAY_KEYS[new Date().getDay()]];
    if (!h) return 'Closed today';
    if (h.open === '00:00' && (h.close === '23:59' || h.close === '24:00')) return 'Open 24 hours';
    return `Today ${h.open}–${h.close}`;
  };

  const directions = (w: WashLocation) => {
    // Universal Google Maps directions URL — opens the native maps chooser on
    // phones and the website on desktop. No API key involved.
    const dest = encodeURIComponent(`${w.address}, ${w.city}, ${w.state} ${w.zip}`);
    openExternal(`https://www.google.com/maps/dir/?api=1&destination=${dest}`);
  };

  return (
    // Wave 3 (Nathan): the page scrolls as ONE unit — the map scrolls up with
    // the content instead of staying pinned. The old Android nested-touch
    // conflict (map views grab touches, pans fight the page scroll) is handled
    // by onInteractionChange: page scroll releases while a finger is on the
    // map. Web needs no such handoff.
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      scrollEnabled={!mapActive}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} enabled={!mapActive} />}
    >
      <ContentContainer maxWidth={720}>
        <View style={styles.mapWrap}>
          <WashMapView
            pins={sorted.map(w => ({ id: w.id, name: w.name, latitude: w.latitude, longitude: w.longitude }))}
            userLocation={userLoc}
            selectedId={selectedId}
            onSelect={setSelectedId}
            accent={colors.primary}
            height={300}
            onInteractionChange={setMapActive}
            onViewportChange={handleViewportChange}
          />
        </View>
        {selected && (
          <Card style={styles.selectedCard}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.selName}>{selected.name}</Text>
                <Text style={styles.selSub}>{selected.businessName}</Text>
                <Text style={styles.selSub}>{selected.address}, {selected.city}, {selected.state} {selected.zip}</Text>
                {todayHours(selected) && <Text style={styles.selHours}>{todayHours(selected)}</Text>}
                {selected.miles != null && <Text style={styles.selMiles}>{selected.miles.toFixed(1)} miles away</Text>}
              </View>
              <Button
                title="Directions"
                size="sm"
                onPress={() => directions(selected)}
                icon={<Ionicons name="navigate" size={16} color={colors.white} />}
              />
            </View>
          </Card>
        )}

        <Text style={styles.sectionTitle}>
          {userLoc ? 'Nearest washes' : 'Washes in the network'}
        </Text>
        {!userLoc && !loading && (
          <Text style={styles.hint}>Allow location access to sort by distance.</Text>
        )}
        {inView.map(w => (
          <TouchableOpacity key={w.id} onPress={() => setSelectedId(w.id)}>
            <Card style={[styles.row, selectedId === w.id && { borderColor: colors.primary, borderWidth: 1 }]}>
              <Ionicons name="water" size={22} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.rowName} numberOfLines={1}>{w.name}</Text>
                <Text style={styles.rowBusiness} numberOfLines={1}>{w.businessName}</Text>
                <Text style={styles.rowSub} numberOfLines={1}>{w.city}, {w.state}{todayHours(w) ? ` · ${todayHours(w)}` : ''}</Text>
              </View>
              {w.miles != null && <Text style={styles.rowMiles}>{w.miles.toFixed(1)} mi</Text>}
              <TouchableOpacity onPress={() => directions(w)} hitSlop={8} style={{ padding: spacing.xs }}>
                <Ionicons name="navigate-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </Card>
          </TouchableOpacity>
        ))}
        {sorted.length === 0 && !loading && (
          <Text style={styles.hint}>No washes to show yet.</Text>
        )}
        {sorted.length > 0 && inView.length === 0 && (
          <Text style={styles.hint}>No washes in the current map view — pan or zoom out to see more.</Text>
        )}
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  mapWrap: { marginBottom: spacing.md },
  selectedCard: { marginTop: 0, borderWidth: 1, borderColor: c.primary + '40' },
  selName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  selSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  selHours: { fontSize: fontSize.xs, color: c.text, marginTop: 2 },
  selMiles: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary, marginTop: 2 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  hint: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', marginBottom: spacing.sm },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm, paddingVertical: spacing.sm },
  rowName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  rowBusiness: { fontSize: fontSize.xs, color: c.primary, marginTop: 1 },
  rowSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  rowMiles: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
